"use client";

import { memo, useState } from "react";
import Link from "next/link";
import { TrendingUp, Clock, ArrowRight, BookOpen, Eye, Heart } from "lucide-react";
import type { CaseStudy } from "@/utils/caseStudiesCatalog";
import { cn } from "@/lib/utils";
import { buildBlogArticleTourUrl } from "@/lib/onboarding-constants";

const TV_LOGO_SLUGS: Record<string, string> = {
  CZR: "caesars",
  QQQ: "invesco",
  SPY: "spdr-sandp500-etf-tr",
  NFLX: "netflix",
  META: "meta-platforms",
  AMD: "advanced-micro-devices",
  PLUG: "plug-power",
  PLD: "prologis",
  ORCL: "oracle",
  SPX: "indices/s-and-p-500",
  NVDA: "nvidia",
  ESPR: "esperion-therapeutics",
};

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
}

interface CaseStudyCardProps {
  study: CaseStudy;
  className?: string;
  compact?: boolean;
  likeCount?: number;
  viewCount?: number;
  /** When true, the card's href is rewritten to the tour URL
   *  (`/blog/{slug}?onboarding_blog_article=1`) so BlogArticleBuddyTour
   *  picks up the next leg of the onboarding chain. */
  tourActive?: boolean;
  /** When true, the card receives `data-tour="trades-first-card"` so
   *  BlogBuddyTour's click_target beat can anchor on it. */
  tourTarget?: boolean;
}

function PureCaseStudyCard({
  study,
  className,
  compact,
  likeCount,
  viewCount,
  tourActive = false,
  tourTarget = false,
}: CaseStudyCardProps) {
  const [isHovered, setIsHovered] = useState(false);
  const isLocked = !study.isPublished;
  const isGuide = study.returnPct === "Guide";
  const cardClasses = cn(
    "group relative block h-full",
    isLocked ? "cursor-default" : "cursor-pointer",
    className
  );
  const cardContent = (
    <div
      className={cn(
        "relative rounded-xl border overflow-hidden h-full flex flex-col",
        "bg-[#0c0c0e]",
        "transition-[transform,border-color,box-shadow] duration-400 ease-[cubic-bezier(0.23,1,0.32,1)]",
        "border-white/[0.06] shadow-[0_2px_12px_rgba(0,0,0,0.5)]",
        !isLocked &&
          "group-hover:border-emerald-500/20 group-hover:shadow-[0_8px_32px_rgba(16,185,129,0.06)] group-hover:-translate-y-1"
      )}
    >
      {/* ── THUMBNAIL ── */}
      <div className={cn("relative w-full overflow-hidden bg-[#0a0a0b] shrink-0", compact ? "aspect-[16/9]" : "aspect-[16/10]")}>
        <img
          src={study.thumbnailUrl}
          alt={study.headline}
          className={cn(
            "absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500",
            isLocked ? "scale-[1.01] opacity-55" : "group-hover:scale-[1.03]"
          )}
          loading="lazy"
        />

        <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0e] via-[#0c0c0e]/20 to-transparent" />

        <div className="absolute top-3 right-3 z-10">
          {isGuide ? (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/65 border border-emerald-500/25 backdrop-blur-md">
              <BookOpen className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-400 text-xs font-bold tracking-wide">
                Tutorial
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/65 border border-emerald-500/25 backdrop-blur-md">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              <span className="text-emerald-400 text-xs font-bold tracking-wide">
                {study.returnPct}
              </span>
            </div>
          )}
        </div>

        <div className="absolute top-3 left-3 z-10">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/65 border border-white/[0.12] backdrop-blur-md">
            {TV_LOGO_SLUGS[study.ticker] && (
              <img
                src={`https://s3-symbol-logo.tradingview.com/${TV_LOGO_SLUGS[study.ticker]}--big.svg`}
                alt={study.ticker}
                className="w-4 h-4 rounded-full"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <span className="text-white text-xs font-bold tracking-wider">
              {`$${study.ticker}`}
            </span>
          </div>
        </div>

        {!isLocked && (
          <div
            className={cn(
              "absolute inset-0 z-20 flex items-center justify-center pointer-events-none",
              "transition-all duration-400 ease-[cubic-bezier(0.23,1,0.32,1)]",
              isHovered ? "opacity-100" : "opacity-0"
            )}
          >
            <div
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-full",
                "transition-all duration-400 ease-[cubic-bezier(0.23,1,0.32,1)]",
                isHovered ? "scale-100" : "scale-[0.7]",
                "bg-black/60 backdrop-blur-sm border border-white/[0.15]",
                "shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
              )}
            >
              <span className="text-white/90 text-sm font-medium">
                {isGuide ? "Read Guide" : "View Trade"}
              </span>
              <ArrowRight className="w-3.5 h-3.5 text-white/90" />
            </div>
          </div>
        )}

        {isLocked && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/45 backdrop-blur-[2px]">
            <div className="rounded-full border border-white/15 bg-black/60 px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-white/90">
              Coming Soon
            </div>
          </div>
        )}
      </div>

      {/* ── CONTENT ── */}
      <div className={cn("px-4 flex flex-col", compact ? "pt-3.5 pb-4 gap-2 flex-1" : "pt-3 pb-3.5 flex-1 gap-2")}>
        <h3 className={cn(
          "text-zinc-100 font-semibold leading-snug transition-colors duration-300 group-hover:text-white",
          compact
            ? "text-[14px] line-clamp-1"
            : "text-base line-clamp-2 min-h-[2.75em]"
        )}>
          {study.headline}
        </h3>

        {!compact && (
          <div className="flex items-center gap-1 flex-wrap">
            {study.tags.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-px rounded text-[9px] font-medium text-zinc-400 bg-white/[0.04] border border-white/[0.06] uppercase tracking-wider"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* ── AUTHOR + META ── */}
        <div className={cn("flex items-center justify-between mt-auto", !compact && "pt-1")}>
          <div className="flex items-center gap-2 min-w-0">
            {study.author && (
              <>
                <img
                  src={study.author.avatar}
                  alt={study.author.name}
                  className="w-5 h-5 rounded-full object-cover border border-white/10 shrink-0"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                />
                <span className="text-[11px] text-zinc-400 font-medium truncate">
                  {study.author.name}
                </span>
              </>
            )}
            <span className="w-[3px] h-[3px] rounded-full bg-zinc-700 shrink-0" />
            <span className="text-[11px] text-zinc-500 flex items-center gap-1 shrink-0">
              <Clock className="w-3 h-3" />
              {study.readTime}
            </span>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <span className="flex items-center gap-1 text-[11px] text-zinc-500">
              <Eye className="w-3 h-3" />
              {formatCount(viewCount ?? study.views)}
            </span>
            <span className="flex items-center gap-1 text-[11px] text-zinc-500">
              <Heart className="w-3 h-3" />
              {formatCount(likeCount ?? study.likes)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return isLocked ? (
    <div className={cardClasses}>{cardContent}</div>
  ) : (
    <Link
      href={
        tourActive ? buildBlogArticleTourUrl(study.slug) : `/blog/${study.slug}`
      }
      data-tour={tourTarget ? "trades-first-card" : undefined}
      className={cardClasses}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {cardContent}
    </Link>
  );
}

export const CaseStudyCard = memo(PureCaseStudyCard);

export function CaseStudyCardSkeleton() {
  return (
    <div className="relative block">
      <div className="rounded-xl border border-white/[0.05] overflow-hidden bg-[#0c0c0e]">
        <div className="w-full aspect-[16/10] bg-zinc-900/50 animate-pulse" />
        <div className="px-4 pt-3 pb-3.5">
          <div className="h-4 w-4/5 bg-zinc-800/60 rounded-md animate-pulse" />
          <div className="h-3 w-3/5 bg-zinc-800/40 rounded-md animate-pulse mt-2" />
          <div className="h-3 w-2/5 bg-zinc-800/30 rounded-md animate-pulse mt-3" />
        </div>
      </div>
    </div>
  );
}
